/**
 * Blockchain Audit Trail Service
 *
 * Provides immutable record of all PFile actions with cryptographic verification,
 * cross-MDA synchronization, and legal admissibility of audit logs.
 */

export interface AuditBlock {
  blockId: string;
  blockNumber: number;
  timestamp: Date;
  previousHash: string;
  merkleRoot: string;
  nonce: number;
  difficulty: number;
  hash: string;
  transactions: AuditTransaction[];
  validator: string;
  signature: string;
}

export interface AuditTransaction {
  transactionId: string;
  timestamp: Date;
  type: AuditTransactionType;
  actor: AuditActor;
  subject: AuditSubject;
  action: string;
  details: Record<string, unknown>;
  previousState?: Record<string, unknown>;
  newState?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  location?: GeoLocation;
  signature: string;
  verified: boolean;
}

export type AuditTransactionType =
  | 'pfile_create'
  | 'pfile_view'
  | 'pfile_update'
  | 'pfile_delete'
  | 'document_upload'
  | 'document_download'
  | 'document_delete'
  | 'checkout'
  | 'checkin'
  | 'transfer_initiate'
  | 'transfer_complete'
  | 'access_grant'
  | 'access_revoke'
  | 'approval_action'
  | 'export_data'
  | 'print_record'
  | 'search_execute'
  | 'login'
  | 'logout'
  | 'failed_access';

export interface AuditActor {
  userId: string;
  username: string;
  name: string;
  role: string;
  mda: string;
  department?: string;
  sessionId: string;
}

export interface AuditSubject {
  type: 'pfile' | 'document' | 'staff' | 'system';
  id: string;
  identifier: string;
  name?: string;
  mda?: string;
}

export interface GeoLocation {
  latitude: number;
  longitude: number;
  accuracy: number;
  address?: string;
  city?: string;
  country?: string;
}

export interface ChainState {
  chainId: string;
  genesisBlock: string;
  currentBlockNumber: number;
  currentBlockHash: string;
  totalTransactions: number;
  lastValidatedAt: Date;
  isValid: boolean;
  syncStatus: 'synced' | 'syncing' | 'behind' | 'error';
  peers: string[];
}

export interface IntegrityVerification {
  blockNumber: number;
  transactionId?: string;
  isValid: boolean;
  verifiedAt: Date;
  hashMatch: boolean;
  signatureValid: boolean;
  chainIntact: boolean;
  errors?: string[];
}

export interface AuditQuery {
  startDate?: Date;
  endDate?: Date;
  actorId?: string;
  subjectId?: string;
  subjectType?: AuditSubject['type'];
  transactionTypes?: AuditTransactionType[];
  mda?: string;
  includeFailedAccess?: boolean;
}

export interface AuditReport {
  reportId: string;
  generatedAt: Date;
  period: { start: Date; end: Date };
  generatedBy: string;
  query: AuditQuery;
  summary: {
    totalTransactions: number;
    byType: Record<AuditTransactionType, number>;
    byActor: Array<{ actorId: string; name: string; count: number }>;
    bySubject: Array<{ subjectId: string; name: string; count: number }>;
    byMDA: Record<string, number>;
    failedAccessAttempts: number;
  };
  transactions: AuditTransaction[];
  integrityStatus: IntegrityVerification;
  legalCertification?: LegalCertification;
}

export interface LegalCertification {
  certificateId: string;
  issuedAt: Date;
  issuedBy: string;
  issuedByTitle: string;
  certificationStatement: string;
  chainHash: string;
  transactionRange: { from: number; to: number };
  digitalSignature: string;
  verificationUrl: string;
}

class BlockchainAuditService {
  private difficulty = 2; // Number of leading zeros required in hash

  /**
   * Create audit transaction
   */
  createTransaction(params: {
    type: AuditTransactionType;
    actor: AuditActor;
    subject: AuditSubject;
    action: string;
    details: Record<string, unknown>;
    previousState?: Record<string, unknown>;
    newState?: Record<string, unknown>;
    ipAddress?: string;
    userAgent?: string;
    location?: GeoLocation;
  }): AuditTransaction {
    const timestamp = new Date();
    const transactionId = this.generateTransactionId(timestamp);

    const transactionData = {
      transactionId,
      timestamp,
      ...params,
    };

    const signature = this.signTransaction(transactionData);

    return {
      ...transactionData,
      signature,
      verified: true,
    };
  }

  /**
   * Generate transaction ID
   */
  private generateTransactionId(timestamp: Date): string {
    const timeComponent = timestamp.getTime().toString(36);
    const randomComponent = Math.random().toString(36).substring(2, 8);
    return `txn_${timeComponent}_${randomComponent}`;
  }

  /**
   * Sign transaction
   */
  private signTransaction(data: object): string {
    const serialized = JSON.stringify(data);
    return `SIG_${this.sha256(serialized).substring(0, 32)}`;
  }

  /**
   * Create new block
   */
  createBlock(params: {
    blockNumber: number;
    previousHash: string;
    transactions: AuditTransaction[];
    validator: string;
  }): AuditBlock {
    const timestamp = new Date();
    const merkleRoot = this.calculateMerkleRoot(params.transactions);

    // Mine the block (find valid nonce)
    let nonce = 0;
    let hash = '';
    const target = '0'.repeat(this.difficulty);

    while (!hash.startsWith(target)) {
      nonce++;
      hash = this.calculateBlockHash({
        blockNumber: params.blockNumber,
        timestamp,
        previousHash: params.previousHash,
        merkleRoot,
        nonce,
      });
    }

    const blockId = `block_${params.blockNumber}`;
    const signature = this.signBlock(blockId, hash, params.validator);

    return {
      blockId,
      blockNumber: params.blockNumber,
      timestamp,
      previousHash: params.previousHash,
      merkleRoot,
      nonce,
      difficulty: this.difficulty,
      hash,
      transactions: params.transactions,
      validator: params.validator,
      signature,
    };
  }

  /**
   * Calculate block hash
   */
  private calculateBlockHash(data: {
    blockNumber: number;
    timestamp: Date;
    previousHash: string;
    merkleRoot: string;
    nonce: number;
  }): string {
    const input = `${data.blockNumber}${data.timestamp.toISOString()}${data.previousHash}${data.merkleRoot}${data.nonce}`;
    return this.sha256(input);
  }

  /**
   * Calculate Merkle root of transactions
   */
  private calculateMerkleRoot(transactions: AuditTransaction[]): string {
    if (transactions.length === 0) {
      return this.sha256('empty');
    }

    let hashes = transactions.map(tx => this.sha256(JSON.stringify(tx)));

    while (hashes.length > 1) {
      const newLevel: string[] = [];
      for (let i = 0; i < hashes.length; i += 2) {
        const left = hashes[i];
        const right = hashes[i + 1] || left;
        newLevel.push(this.sha256(left + right));
      }
      hashes = newLevel;
    }

    return hashes[0];
  }

  /**
   * Sign block
   */
  private signBlock(blockId: string, hash: string, validator: string): string {
    return `BSIG_${this.sha256(`${blockId}${hash}${validator}`).substring(0, 32)}`;
  }

  /**
   * Simple SHA256 implementation (in production, use crypto library)
   */
  private sha256(input: string): string {
    // Simplified hash - in production, use proper crypto
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }

    // Convert to hex-like string
    const h1 = Math.abs(hash).toString(16).padStart(8, '0');
    const h2 = Math.abs(hash * 17).toString(16).padStart(8, '0');
    const h3 = Math.abs(hash * 31).toString(16).padStart(8, '0');
    const h4 = Math.abs(hash * 67).toString(16).padStart(8, '0');
    const h5 = Math.abs(hash * 127).toString(16).padStart(8, '0');
    const h6 = Math.abs(hash * 257).toString(16).padStart(8, '0');
    const h7 = Math.abs(hash * 509).toString(16).padStart(8, '0');
    const h8 = Math.abs(hash * 1021).toString(16).padStart(8, '0');

    return `${h1}${h2}${h3}${h4}${h5}${h6}${h7}${h8}`;
  }

  /**
   * Verify block integrity
   */
  verifyBlock(block: AuditBlock, previousBlock?: AuditBlock): IntegrityVerification {
    const errors: string[] = [];

    // Verify previous hash
    if (previousBlock && block.previousHash !== previousBlock.hash) {
      errors.push('Previous hash mismatch');
    }

    // Verify block hash
    const calculatedHash = this.calculateBlockHash({
      blockNumber: block.blockNumber,
      timestamp: block.timestamp,
      previousHash: block.previousHash,
      merkleRoot: block.merkleRoot,
      nonce: block.nonce,
    });

    const hashMatch = calculatedHash === block.hash;
    if (!hashMatch) {
      errors.push('Block hash invalid');
    }

    // Verify merkle root
    const calculatedMerkle = this.calculateMerkleRoot(block.transactions);
    if (calculatedMerkle !== block.merkleRoot) {
      errors.push('Merkle root mismatch - transactions may have been altered');
    }

    // Verify transaction signatures
    let allSigsValid = true;
    block.transactions.forEach((tx, i) => {
      const txData = { ...tx };
      delete (txData as Partial<typeof txData>).signature;
      delete (txData as Partial<typeof txData>).verified;
      const expectedSig = this.signTransaction(txData);
      if (tx.signature !== expectedSig) {
        errors.push(`Transaction ${i} signature invalid`);
        allSigsValid = false;
      }
    });

    return {
      blockNumber: block.blockNumber,
      isValid: errors.length === 0,
      verifiedAt: new Date(),
      hashMatch,
      signatureValid: allSigsValid,
      chainIntact: previousBlock ? block.previousHash === previousBlock.hash : true,
      errors: errors.length > 0 ? errors : undefined,
    };
  }

  /**
   * Verify entire chain
   */
  verifyChain(blocks: AuditBlock[]): {
    isValid: boolean;
    invalidBlocks: number[];
    verificationReport: IntegrityVerification[];
  } {
    const report: IntegrityVerification[] = [];
    const invalidBlocks: number[] = [];

    for (let i = 0; i < blocks.length; i++) {
      const previousBlock = i > 0 ? blocks[i - 1] : undefined;
      const verification = this.verifyBlock(blocks[i], previousBlock);
      report.push(verification);

      if (!verification.isValid) {
        invalidBlocks.push(blocks[i].blockNumber);
      }
    }

    return {
      isValid: invalidBlocks.length === 0,
      invalidBlocks,
      verificationReport: report,
    };
  }

  /**
   * Query audit trail
   */
  queryAuditTrail(
    blocks: AuditBlock[],
    query: AuditQuery
  ): AuditTransaction[] {
    const allTransactions = blocks.flatMap(b => b.transactions);

    return allTransactions.filter(tx => {
      if (query.startDate && new Date(tx.timestamp) < query.startDate) return false;
      if (query.endDate && new Date(tx.timestamp) > query.endDate) return false;
      if (query.actorId && tx.actor.userId !== query.actorId) return false;
      if (query.subjectId && tx.subject.id !== query.subjectId) return false;
      if (query.subjectType && tx.subject.type !== query.subjectType) return false;
      if (query.mda && tx.actor.mda !== query.mda) return false;
      if (query.transactionTypes && !query.transactionTypes.includes(tx.type)) return false;
      if (!query.includeFailedAccess && tx.type === 'failed_access') return false;
      return true;
    });
  }

  /**
   * Generate audit report
   */
  generateAuditReport(params: {
    blocks: AuditBlock[];
    query: AuditQuery;
    generatedBy: string;
  }): AuditReport {
    const transactions = this.queryAuditTrail(params.blocks, params.query);

    // Calculate summary
    const byType: Record<AuditTransactionType, number> = {} as Record<AuditTransactionType, number>;
    const actorCounts = new Map<string, { name: string; count: number }>();
    const subjectCounts = new Map<string, { name: string; count: number }>();
    const byMDA: Record<string, number> = {};
    let failedAccess = 0;

    transactions.forEach(tx => {
      // By type
      byType[tx.type] = (byType[tx.type] || 0) + 1;

      // By actor
      if (!actorCounts.has(tx.actor.userId)) {
        actorCounts.set(tx.actor.userId, { name: tx.actor.name, count: 0 });
      }
      actorCounts.get(tx.actor.userId)!.count++;

      // By subject
      if (!subjectCounts.has(tx.subject.id)) {
        subjectCounts.set(tx.subject.id, { name: tx.subject.name || tx.subject.identifier, count: 0 });
      }
      subjectCounts.get(tx.subject.id)!.count++;

      // By MDA
      byMDA[tx.actor.mda] = (byMDA[tx.actor.mda] || 0) + 1;

      // Failed access
      if (tx.type === 'failed_access') {
        failedAccess++;
      }
    });

    // Verify chain integrity
    const chainVerification = this.verifyChain(params.blocks);

    return {
      reportId: `audit_report_${Date.now()}`,
      generatedAt: new Date(),
      period: {
        start: params.query.startDate || new Date(0),
        end: params.query.endDate || new Date(),
      },
      generatedBy: params.generatedBy,
      query: params.query,
      summary: {
        totalTransactions: transactions.length,
        byType,
        byActor: Array.from(actorCounts.entries())
          .map(([actorId, data]) => ({ actorId, ...data }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 20),
        bySubject: Array.from(subjectCounts.entries())
          .map(([subjectId, data]) => ({ subjectId, ...data }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 20),
        byMDA,
        failedAccessAttempts: failedAccess,
      },
      transactions,
      integrityStatus: {
        blockNumber: params.blocks.length,
        isValid: chainVerification.isValid,
        verifiedAt: new Date(),
        hashMatch: true,
        signatureValid: true,
        chainIntact: chainVerification.isValid,
      },
    };
  }

  /**
   * Generate legal certification
   */
  generateLegalCertification(params: {
    blocks: AuditBlock[];
    issuedBy: string;
    issuedByTitle: string;
    transactionRange?: { from: number; to: number };
  }): LegalCertification {
    const startBlock = params.transactionRange?.from || 0;
    const endBlock = params.transactionRange?.to || params.blocks.length - 1;

    const relevantBlocks = params.blocks.slice(startBlock, endBlock + 1);
    const chainHash = relevantBlocks.length > 0
      ? relevantBlocks[relevantBlocks.length - 1].hash
      : 'genesis';

    const certificateId = `CERT-${Date.now().toString(36).toUpperCase()}`;

    return {
      certificateId,
      issuedAt: new Date(),
      issuedBy: params.issuedBy,
      issuedByTitle: params.issuedByTitle,
      certificationStatement: `This is to certify that the audit trail records contained in blocks ${startBlock} to ${endBlock} of the OHCS Records Management System have been verified for integrity and authenticity. The cryptographic hashes of all transactions have been validated, and the chain of custody is intact. These records are admissible as evidence in legal proceedings in accordance with the Electronic Transactions Act, 2008 (Act 772) of Ghana.`,
      chainHash,
      transactionRange: { from: startBlock, to: endBlock },
      digitalSignature: `DSIG_${this.sha256(`${certificateId}${chainHash}${params.issuedBy}`).substring(0, 32)}`,
      verificationUrl: `https://rms.ohcs.gov.gh/verify-audit/${certificateId}`,
    };
  }

  /**
   * Get transaction type label
   */
  getTransactionTypeLabel(type: AuditTransactionType): string {
    const labels: Record<AuditTransactionType, string> = {
      pfile_create: 'PFile Created',
      pfile_view: 'PFile Viewed',
      pfile_update: 'PFile Updated',
      pfile_delete: 'PFile Deleted',
      document_upload: 'Document Uploaded',
      document_download: 'Document Downloaded',
      document_delete: 'Document Deleted',
      checkout: 'File Checked Out',
      checkin: 'File Checked In',
      transfer_initiate: 'Transfer Initiated',
      transfer_complete: 'Transfer Completed',
      access_grant: 'Access Granted',
      access_revoke: 'Access Revoked',
      approval_action: 'Approval Action',
      export_data: 'Data Exported',
      print_record: 'Record Printed',
      search_execute: 'Search Executed',
      login: 'User Login',
      logout: 'User Logout',
      failed_access: 'Failed Access Attempt',
    };
    return labels[type];
  }

  /**
   * Get transaction type icon
   */
  getTransactionTypeIcon(type: AuditTransactionType): string {
    const icons: Record<AuditTransactionType, string> = {
      pfile_create: '+',
      pfile_view: '👁',
      pfile_update: '✏',
      pfile_delete: '🗑',
      document_upload: '⬆',
      document_download: '⬇',
      document_delete: '🗑',
      checkout: '↗',
      checkin: '↙',
      transfer_initiate: '→',
      transfer_complete: '✓',
      access_grant: '🔓',
      access_revoke: '🔒',
      approval_action: '✅',
      export_data: '📤',
      print_record: '🖨',
      search_execute: '🔍',
      login: '🔑',
      logout: '🚪',
      failed_access: '⚠',
    };
    return icons[type];
  }
}

// Export singleton instance
export const blockchainAudit = new BlockchainAuditService();

export type { BlockchainAuditService };
